/** Central mapping: legacy ?tab= ↔ new paths (UX1). */

export type AccountingViewId =
  | 'dashboard'
  | 'chart'
  | 'periods'
  | 'closeWizard'
  | 'journal'
  | 'ledger'
  | 'trial'
  | 'income'
  | 'balance'
  | 'cashflow'
  | 'costCenters'
  | 'businessUnits'
  | 'inflation'
  | 'workingPapers'
  | 'imports'
  | 'multicurrency'
  | 'integrity'
  | 'mappings'
  | 'fiscalYears';

export const TAB_TO_PATH: Record<string, string> = {
  dashboard: '/accounting',
  chart: '/accounting/masters/chart',
  journal: '/accounting/books/journal',
  ledger: '/accounting/books/ledger',
  trial: '/accounting/books/trial',
  income: '/accounting/statements/income',
  balance: '/accounting/statements/balance',
  cashflow: '/accounting/statements/cashflow',
  periods: '/accounting/close/periods',
  integrity: '/accounting/close/integrity',
  workingPapers: '/accounting/close/working-papers',
  costCenters: '/accounting/masters/cost-centers',
  businessUnits: '/accounting/masters/business-units',
  imports: '/accounting/tools/imports',
  multicurrency: '/accounting/tools/fx',
  inflation: '/accounting/tools/inflation',
};

export const PATH_TO_VIEW: Record<string, AccountingViewId> = {
  '/accounting': 'dashboard',
  '/accounting/books/journal': 'journal',
  '/accounting/books/ledger': 'ledger',
  '/accounting/books/trial': 'trial',
  '/accounting/statements/income': 'income',
  '/accounting/statements/balance': 'balance',
  '/accounting/statements/cashflow': 'cashflow',
  '/accounting/close': 'closeWizard',
  '/accounting/close/periods': 'periods',
  '/accounting/close/fiscal-years': 'fiscalYears',
  '/accounting/close/working-papers': 'workingPapers',
  '/accounting/close/integrity': 'integrity',
  '/accounting/masters/chart': 'chart',
  '/accounting/masters/cost-centers': 'costCenters',
  '/accounting/masters/business-units': 'businessUnits',
  '/accounting/masters/mappings': 'mappings',
  '/accounting/tools/imports': 'imports',
  '/accounting/tools/fx': 'multicurrency',
  '/accounting/tools/inflation': 'inflation',
};

export function legacyTabToPath(tab: string | null | undefined): string | null {
  if (!tab) return null;
  return TAB_TO_PATH[tab] ?? null;
}

export function viewFromPathname(pathname: string): AccountingViewId | null {
  if (PATH_TO_VIEW[pathname]) return PATH_TO_VIEW[pathname];
  // normalize trailing slash
  const p = pathname.replace(/\/$/, '') || '/';
  return PATH_TO_VIEW[p] ?? null;
}

export type Crumb = { label: string; to?: string };

export function breadcrumbsForPath(pathname: string): Crumb[] {
  const base: Crumb = { label: 'Contabilidad', to: '/accounting' };
  const map: Record<string, Crumb[]> = {
    '/accounting': [base, { label: 'Inicio' }],
    '/accounting/books/journal': [base, { label: 'Libros', to: '/accounting/books/journal' }, { label: 'Diario' }],
    '/accounting/books/ledger': [base, { label: 'Libros' }, { label: 'Mayor' }],
    '/accounting/books/trial': [base, { label: 'Libros' }, { label: 'Sumas y saldos' }],
    '/accounting/statements/income': [base, { label: 'EEFF' }, { label: 'Estado de resultados' }],
    '/accounting/statements/balance': [base, { label: 'EEFF' }, { label: 'Estado patrimonial' }],
    '/accounting/statements/cashflow': [base, { label: 'EEFF' }, { label: 'Flujo de fondos' }],
    '/accounting/close': [base, { label: 'Cierre' }, { label: 'Cierre mensual' }],
    '/accounting/close/periods': [base, { label: 'Cierre' }, { label: 'Períodos históricos' }],
    '/accounting/close/fiscal-years': [base, { label: 'Cierre' }, { label: 'Ejercicios' }],
    '/accounting/close/working-papers': [base, { label: 'Cierre' }, { label: 'Papeles de trabajo' }],
    '/accounting/close/integrity': [base, { label: 'Cierre' }, { label: 'Detalle técnico' }],
    '/accounting/masters/chart': [base, { label: 'Configuración' }, { label: 'Plan de cuentas' }],
    '/accounting/masters/cost-centers': [base, { label: 'Configuración' }, { label: 'Centros de costo' }],
    '/accounting/masters/business-units': [base, { label: 'Configuración' }, { label: 'Unidades de negocio' }],
    '/accounting/masters/mappings': [base, { label: 'Configuración' }, { label: 'Mapeos contables' }],
    '/accounting/tools/imports': [base, { label: 'Herramientas' }, { label: 'Importaciones' }],
    '/accounting/tools/fx': [base, { label: 'Herramientas' }, { label: 'Multimoneda' }],
    '/accounting/tools/inflation': [base, { label: 'Herramientas' }, { label: 'Ajuste por inflación' }],
  };
  const p = pathname.replace(/\/$/, '') || '/accounting';
  return map[p] ?? [base];
}
